import { useEffect, useMemo, useState } from 'react'
import { collection, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore'
import type { LapDoc } from '@racedash/shared'
import { db } from '../lib/firebase'
import { useReplayOf } from './replayContext'

/**
 * Every lap of every car in a session, for the charts that want the field.
 *
 * The race chart and the pace charts draw the whole field, so they cannot use
 * useCarsLaps and its six-car cap. But a single query over the session is not
 * possible either: Firestore refuses a listener whose limit exceeds ten
 * thousand documents, and a race is more than that. So the session is read
 * in bands of fifty laps, each its own listener, each well under the cap for
 * any field we have seen (a hundred cars over fifty laps is five thousand).
 * The number of bands comes from the highest lap number at the moment of
 * subscribing; the last band is left open above, so a live session keeps
 * growing into it.
 *
 * Live, like every listener here: the field moves as laps complete.
 */
const bandSize = 50
// What one listener may hold. Under Firestore's ceiling with room to spare.
const bandCap = 9_999

export interface FieldLaps {
  laps: LapDoc[]
  /** The open-ended last band filled to its cap, so the tail is missing. */
  truncated: boolean
  /** Why there is nothing, when a listener refused rather than found nothing. */
  error: string | null
  /** Still waiting for every band's first snapshot. */
  loading: boolean
}

const empty: FieldLaps = { laps: [], truncated: false, error: null, loading: false }

export function useFieldLaps(streamSessionId: string | null): FieldLaps {
  // During a replay, the field as it stood at the replayed instant. The replay
  // has already read every lap, so this opens nothing of its own.
  const replay = useReplayOf(streamSessionId)
  const live = useFieldLapsLive(replay ? null : streamSessionId)
  const laps = replay?.frame.laps
  const replayed = useMemo(() => (laps ? { laps, truncated: false, error: null, loading: false } : null), [laps])
  return replayed ?? live
}

/** Every lap of the session as stored now, whatever is being replayed. */
export function useFieldLapsLive(streamSessionId: string | null): FieldLaps {
  const [state, setState] = useState<FieldLaps>(empty)

  useEffect(() => {
    if (!streamSessionId) {
      setState(empty)
      return
    }
    setState({ ...empty, loading: true })
    const laps = collection(db, 'streamSessions', streamSessionId, 'laps')
    let cancelled = false
    const unsubs: (() => void)[] = []

    void (async () => {
      let maxLap: number
      try {
        const top = await getDocs(query(laps, orderBy('lap', 'desc'), limit(1)))
        maxLap = Math.max(1, (top.docs[0]?.data() as LapDoc | undefined)?.lap ?? 1)
      } catch (err) {
        if (!cancelled) setState({ ...empty, error: (err as Error).message })
        return
      }
      if (cancelled) return

      const bandCount = Math.ceil(maxLap / bandSize)
      const byBand = new Map<number, LapDoc[]>()
      let truncated = false
      const publish = () => {
        if (byBand.size < bandCount) return
        const all: LapDoc[] = []
        for (let i = 0; i < bandCount; i++) for (const l of byBand.get(i) ?? []) all.push(l)
        setState({ laps: all, truncated, error: null, loading: false })
      }

      for (let i = 0; i < bandCount; i++) {
        const from = i * bandSize + 1
        const last = i === bandCount - 1
        const q = last
          ? query(laps, where('lap', '>=', from), orderBy('lap', 'asc'), limit(bandCap))
          : query(laps, where('lap', '>=', from), where('lap', '<=', from + bandSize - 1), orderBy('lap', 'asc'), limit(bandCap))
        unsubs.push(
          onSnapshot(
            q,
            (snap) => {
              byBand.set(i, snap.docs.map((d) => d.data() as LapDoc))
              if (last) truncated = snap.size >= bandCap
              publish()
            },
            // Surfaced, not swallowed. A refused query looks exactly like a
            // session with no laps, and "nothing to plot" then sends everybody
            // to the wrong place to look.
            (err) => setState({ ...empty, error: err.message }),
          ),
        )
      }
    })()

    return () => {
      cancelled = true
      unsubs.forEach((u) => u())
    }
  }, [streamSessionId])

  return state
}
